// StatusBadges.tsx — the status vocabulary of the three things docdaemon
// tracks (a repo, a run, a job), as three badges in one file because they must
// agree: a run that reads FAILED red has to make the repo above it read ERROR
// red too, and a reader who learns the colours once should not have to relearn
// them one route later. Each mapping is a total Record over its union, so
// adding a state to api.ts fails the typecheck here rather than rendering a
// grey chip nobody notices.
import { StatusBadge } from 'common';
import type { StatusTone } from 'common';
import type { JobStatus, RepoStatus, RunStatus } from '../api';

// Green is "nothing to do and nothing wrong" — an idle repo is up to date, a
// complete run committed. Blue-ish tones mean work in flight. Neutral is the
// absence of a state rather than a quiet one: a repo nobody has indexed yet
// and a run the daemon decided had no work are both facts, not warnings.
const REPO_TONES: Record<RepoStatus, StatusTone> = {
  'idle': 'success',
  'queued': 'info',
  'running': 'primary',
  'error': 'danger',
  'never-indexed': 'neutral',
};

const RUN_TONES: Record<RunStatus, StatusTone> = {
  'pending': 'neutral',
  'running': 'primary',
  // Committing is the tail of a successful run — the documents are written and
  // it is pushing them back as its own author — so it is distinguished from
  // running without being distinguished from success.
  'committing': 'info',
  'complete': 'success',
  'failed': 'danger',
  'skipped': 'neutral',
};

const JOB_TONES: Record<JobStatus, StatusTone> = {
  'pending': 'neutral',
  'running': 'primary',
  'done': 'success',
  'failed': 'danger',
  'skipped': 'neutral',
};

export function RepoStatusBadge({ status }: { status: RepoStatus }) {
  return <StatusBadge tone={REPO_TONES[status]}>{status}</StatusBadge>;
}

export function RunStatusBadge({ status }: { status: RunStatus }) {
  return <StatusBadge tone={RUN_TONES[status]}>{status}</StatusBadge>;
}

export function JobStatusBadge({ status }: { status: JobStatus }) {
  return <StatusBadge tone={JOB_TONES[status]}>{status}</StatusBadge>;
}
